/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faCaretDown,
  faBars,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import "../../static/css/common/header.css";
import { Helmet } from "react-helmet-async";
import { api } from "@/api";
import { useTranslation } from "react-i18next";
import { AddLangToPath } from "@/helper/AddLanguagePath";
import { getCanonicalUrl, getHreflangAlternates } from "@/utils/canonicalUrl";
import { URL_LANGUAGES } from "@/lib/constants";
import { MenuBackIcon } from "@/components/common/icons";

import CurrencyConverter from "@/helper/CurrencyConverter";
import { safeSetLang } from "@/helper/safeLocalStorage";
import {
  isDeskshare,
  getCartUrl,
  getAccountUrl,
  getDashboardUrl,
  getAuthLoginUrl,
} from "@/utils/siteConfig";
import {
  LanguageSwitcher,
  useLanguageInit,
  ProductDropdown,
  NavDropdown,
  MobileNavbar,
  CartIcon,
  BREAKPOINT,
  LANGUAGES,
  EXCLUDED_PRODUCTS,
} from "./HeaderComponents";
import type {
  ProductData,
  NavItem,
  NavDropdownSection,
} from "./HeaderComponents";
import { useWindowWidth } from "@/hooks/useWindowWidth";

library.add(faAngleDown, faCaretDown, faBars, faXmark);

const Header: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useLanguageInit();
  const [productData, setProductData] = useState<ProductData | null>(null);
  const windowWidth = useWindowWidth(BREAKPOINT);
  const [openSupport, setOpenSupport] = useState(false);
  const [openResources, setOpenResources] = useState(false);
  const [openCompany, setOpenCompany] = useState(false);

  const { isuser_loggedin } = api.auth.getShoppingCartLoginStatus();

  const navItems: NavItem[] = useMemo(
    () => [
      {
        title: t("header.support.title"),
        items: [
          {
            title: t("header.support.forum.title"),
            link: "/forums",
            forceHardNav: true, // Traefik routes /forums to NodeBB; React Router bypasses Traefik
          },
          {
            title: t("header.support.technical_support.title"),
            link: AddLangToPath("/product-support/"),
          },
          {
            title: t("header.support.lost_license.title"),
            link: AddLangToPath("/lost-license-key/"),
          },
        ],
      },
      {
        title: t("header.resources.title"),
        items: [
          {
            title: t("header.resources.articles.title"),
            link: AddLangToPath("/articles"),
          },
          {
            title: t("header.resources.video_tutorials.title"),
            link: AddLangToPath("/tutorials-manuals"),
          },
        ],
      },
      {
        title: t("header.company.title"),
        items: [
          {
            title: t("header.company.contact.title"),
            link: AddLangToPath("/contact-us/"),
          },
          {
            title: t("header.company.about.title"),
            link: AddLangToPath("/about-deskshare/"),
          },
        ],
      },
    ],
    [t],
  );

  const [mobileNavbarItems, setMobileNavbarItems] =
    useState<NavItem[]>(navItems);

  CurrencyConverter();

  useEffect(() => {
    if (windowWidth > BREAKPOINT) {
      setOpenMenu(false);
      document.body.style.overflow = "auto";
      return;
    }
    document.body.style.overflow = openMenu ? "hidden" : "auto";
  }, [windowWidth, openMenu]);

  useEffect(() => {
    let cancelled = false;

    const loadProducts = async (): Promise<void> => {
      const data = await api.products.getHeader<ProductData>();
      if (cancelled || !data) return;
      setProductData(data);

      const allProducts = Object.values(data).flatMap((products) => products);
      const eligibleProducts = allProducts.filter(
        (product) => !EXCLUDED_PRODUCTS.includes(product.product_name),
      );

      const uniqueProducts = new Map<string, { title: string; link: string }>();
      eligibleProducts.forEach((product) => {
        if (!uniqueProducts.has(product.product_slug)) {
          uniqueProducts.set(product.product_slug, {
            title: product.product_name,
            link: AddLangToPath(`/${product.product_slug}`),
          });
        }
      });

      if (!cancelled) {
        setMobileNavbarItems([
          {
            title: t("header.product"),
            items: Array.from(uniqueProducts.values()),
          },
          ...navItems,
        ]);
      }
    };

    loadProducts().catch(() => {
      // ApiClient has already reported this error to Sentry.
    });

    return () => {
      cancelled = true;
    };
  }, [t, navItems]);

  // Bug 6 fix: logo click on homepage now triggers a full page reload so all state
  // is cleared and homepage data is re-fetched. window.location.reload() replaces
  // the previous navigate(replace:true) which was a no-op (SPA route replacement).
  // This also closes HANDOFF DD-5 (homepage re-fetch awaiting product decision).
  const closeMobileMenuRef = React.useRef<(() => void) | null>(null);
  const isMobileRef = React.useRef<boolean>(false);

  const handleLogoClick = useCallback(
    (e: React.MouseEvent): void => {
      const homePath = AddLangToPath("/");
      if (location.pathname === homePath) {
        e.preventDefault();
        window.location.reload(); // full reload — clears state, re-runs data fetch
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      if (isMobileRef.current) closeMobileMenuRef.current?.();
    },
    [location.pathname],
  );

  const handleLanguage = useCallback(
    (lang: string): void => {
      if (!lang) return;

      const langExists = LANGUAGES.some((l) => l.id === lang);
      if (langExists) {
        setSelectedLanguage(lang);
        safeSetLang(lang);

        let mypathArr: string[] = window.location.pathname.split("/");
        if (LANGUAGES.some((l) => l.id === mypathArr[1])) {
          mypathArr = mypathArr.slice(2);
        }
        const mypath = mypathArr.filter(Boolean).join("/");

        // AF-2: window.location.replace() prevents the current URL being added to
        // browser history (no spurious back-button entry for the pre-language-switch URL).
        // A hard reload is architecturally required here: this codebase uses URL-based
        // language routing and i18next-http-backend (loads locale JSON on demand).
        window.location.replace(
          lang === "en" ? `/${mypath}` : `/${lang}/${mypath}`,
        );
      } else {
        setSelectedLanguage("en");
        safeSetLang("en");
      }
    },
    [setSelectedLanguage],
  );

  const openMenuBar = useCallback((): void => {
    setOpenMenu((prev) => {
      document.body.style.overflow = prev ? "auto" : "hidden";
      return !prev;
    });
  }, []);

  const handleNavClick = useCallback(() => {
    if (windowWidth < BREAKPOINT) {
      openMenuBar();
    }
  }, [windowWidth, openMenuBar]);

  const closeMobileMenu = useCallback(() => {
    setOpenMenu(false);
    document.body.style.overflow = "auto";
  }, []);
  // DD-3: keep ref in sync so handleLogoClick (declared before this) can call it
  closeMobileMenuRef.current = closeMobileMenu;

  const navDropdownSections: NavDropdownSection[] = useMemo(
    () => [
      {
        title: "support",
        openState: openSupport,
        setOpenState: setOpenSupport,
        links:
          selectedLanguage === "en"
            ? [
                {
                  to: "/forums",
                  titleKey: "header.support.forum.title",
                  descKey: "header.support.forum.description",
                  isDjangoPage: true, // Traefik routes /forums → NodeBB; must be <a href>
                },
                {
                  to: AddLangToPath("/product-support/"),
                  titleKey: "header.support.technical_support.title",
                  descKey: "header.support.technical_support.description",
                },
                {
                  to: AddLangToPath("/lost-license-key/"),
                  titleKey: "header.support.lost_license.title",
                  descKey: "header.support.lost_license.description",
                },
              ]
            : [
                // DD-24: Forum link shown for all languages — forum is English-only
                // but international users benefit from knowing it exists.
                {
                  to: "/forums",
                  titleKey: "header.support.forum.title",
                  descKey: "header.support.forum.description",
                  isDjangoPage: true, // Traefik routes /forums → NodeBB; must be <a href>
                },
                {
                  to: AddLangToPath("/product-support/"),
                  titleKey: "header.support.technical_support.title",
                  descKey: "header.support.technical_support.description",
                },
                {
                  to: AddLangToPath("/lost-license-key/"),
                  titleKey: "header.support.lost_license.title",
                  descKey: "header.support.lost_license.description",
                },
              ],
      },
      {
        title: "resources",
        openState: openResources,
        setOpenState: setOpenResources,
        links: [
          {
            to: AddLangToPath("/articles"),
            titleKey: "header.resources.articles.title",
            descKey: "header.resources.articles.description",
          },
          {
            to: AddLangToPath("/tutorials-manuals"),
            titleKey: "header.resources.video_tutorials.title",
            descKey: "header.resources.video_tutorials.description",
          },
        ],
      },
      {
        title: "company",
        openState: openCompany,
        setOpenState: setOpenCompany,
        links: [
          {
            to: AddLangToPath("/contact-us/"),
            titleKey: "header.company.contact.title",
            descKey: "header.company.contact.description",
          },
          {
            to: AddLangToPath("/about-deskshare/"),
            titleKey: "header.company.about.title",
            descKey: "header.company.about.description",
          },
        ],
      },
    ],
    [openSupport, openResources, openCompany, selectedLanguage],
  );

  const isMobile = windowWidth < BREAKPOINT;
  // DD-3: keep ref in sync so handleLogoClick can read current value
  isMobileRef.current = isMobile;

  // DD-15: Both desktop and mobile carts now use getCartUrl() — consistent with mobile
  // and with the rest of the codebase. basket.aspx was a legacy hardcoded URL.
  const desktopCartHref = getCartUrl(selectedLanguage);

  const mobileCartHref = getCartUrl(selectedLanguage);

  const authHref = isuser_loggedin
    ? isDeskshare()
      ? getAccountUrl(selectedLanguage)
      : getDashboardUrl(selectedLanguage)
    : isDeskshare()
      ? getAuthLoginUrl()
      : getDashboardUrl(selectedLanguage);

  return (
    <header>
      <Helmet>
        <html lang={selectedLanguage}></html>
        <meta name="description" content={t("meta.site_description")} />
        <meta property="og:title" content="Deskshare" />
        <meta property="og:site_name" content="Deskshare" />
        <meta property="og:description" content={t("meta.site_description")} />
        <meta
          property="og:image"
          content={`${window.location.origin}/images/deskshare_logo.png`}
        />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:url" content={getCanonicalUrl()} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Deskshare" />
        <meta name="twitter:description" content={t("meta.site_description")} />
        <meta
          name="twitter:image"
          content={`${window.location.origin}/images/deskshare_logo512.png`}
        />
        <link rel="canonical" href={getCanonicalUrl()} />
        {/* hreflang alternate links — one per locale including x-default and en.
            Tells Google each URL is a language variant, not duplicate content.
            getHreflangAlternates() strips any existing lang prefix from the
            current pathname before rebuilding each locale URL — so visiting
            /de/screen-recorder produces /fr/screen-recorder for French, never
            /fr/de/screen-recorder. */}
        {getHreflangAlternates(
          window.location.pathname,
          window.location.origin,
          URL_LANGUAGES,
        ).map(({ hrefLang, href }) => (
          <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />
        ))}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Deskshare",
            url: "https://www.deskshare.com",
            logo: "https://www.deskshare.com/images/deskshare_logo.png",
            sameAs: [
              "https://www.facebook.com/people/Deskshare/100064110930535/",
              "https://www.youtube.com/@DeskShareInc",
              "https://www.linkedin.com/company/deskshare",
            ],
          })}
        </script>
      </Helmet>
      <nav
        className="navigation"
        aria-label={t("accessibility.main_navigation")}
        style={
          isMobile && openMenu
            ? { position: "fixed", width: "100%", zIndex: "100" }
            : { position: "relative", width: "100%" }
        }
      >
        <div className="navbar">
          {isMobile && (
            <button
              type="button"
              className="menu-bar"
              onClick={() => setOpenMenu(!openMenu)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") openMenuBar();
              }}
              aria-label={t("accessibility.menu")}
              aria-expanded={openMenu}
            >
              {!openMenu ? (
                <FontAwesomeIcon icon={faBars} className="menu-button" />
              ) : (
                <MenuBackIcon className="menu-button" />
              )}
            </button>
          )}
          <div className="content-left">
            <Link
              to={AddLangToPath("/")}
              className="deskshare-logo"
              onClick={handleLogoClick}
            >
              <LazyLoadImage
                className="logo"
                alt={t("accessibility.deskshare_logo")}
                src="/images/deskshare_logo.svg"
              />
            </Link>
          </div>

          <div className={!openMenu || !isMobile ? "content" : "hidden"}>
            <div className="content-center">
              <ProductDropdown
                productData={productData}
                onNavClick={handleNavClick}
              />
              <Link
                to={AddLangToPath("/download")}
                className="items"
                id="download"
              >
                <span>{t("header.download")}</span>
              </Link>
              <NavDropdown
                sections={navDropdownSections}
                selectedLanguage={selectedLanguage}
                onNavClick={handleNavClick}
              />
            </div>
            <div className="auth">
              <CartIcon href={desktopCartHref} />
              <span className="auth-separator" aria-hidden="true" />
              <a className="signin" href={authHref}>
                {isuser_loggedin ? t("header.my_account") : t("header.sign_in")}
              </a>
            </div>
          </div>

          {isMobile && (
            <CartIcon
              href={mobileCartHref}
              className="cart-btn mobile-cart-btn"
            />
          )}
          <LanguageSwitcher
            selectedLanguage={selectedLanguage}
            onLanguageChange={handleLanguage}
          />
        </div>

        <button
          type="button"
          className={`overlay-button ${!openMenu || !isMobile ? "" : "blur-background"}`}
          onClick={() => setOpenMenu(false)}
          aria-label={
            openMenu
              ? t("accessibility.close_menu")
              : t("accessibility.open_menu")
          }
          tabIndex={openMenu && isMobile ? 0 : -1}
        ></button>

        {isMobile && openMenu && (
          <MobileNavbar
            productData={productData}
            navItems={navItems}
            mobileNavbarItems={mobileNavbarItems}
            isLoggedIn={isuser_loggedin}
            onClose={closeMobileMenu}
          />
        )}
      </nav>
    </header>
  );
};

export default Header;
